import React, { useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Button,
  Card,
  ColorPicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { callCenterSubjectsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import CallSubjectChip from '../../components/call-center/subjects/CallSubjectChip';
import { refreshCallSubjects, useCallSubjects } from '../../components/call-center/subjects/useCallSubjects';
import {
  SUBJECT_COLOR_PRESETS,
  nextSubjectColor,
  normalizeHex,
} from '../../components/call-center/subjects/subjectColors';

/** انتخاب رنگ: پالت آماده + رنگ دلخواه */
const SubjectColorField = ({ value, onChange }) => {
  const current = normalizeHex(value);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {SUBJECT_COLOR_PRESETS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange?.(color)}
          className="w-7 h-7 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
          style={{ backgroundColor: color, borderColor: current === color ? '#111827' : 'transparent' }}
          aria-label={color}
        >
          {current === color && <CheckOutlined className="text-white text-xs" />}
        </button>
      ))}
      <Tooltip title="رنگ دلخواه">
        <ColorPicker
          value={current}
          disabledAlpha
          onChangeComplete={(color) => onChange?.(color.toHexString())}
          size="small"
        />
      </Tooltip>
    </div>
  );
};

const CallCenterSubjects = () => {
  const { subjects, loading, reload } = useCallSubjects();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form] = Form.useForm();

  const previewName = Form.useWatch('name', form);
  const previewColor = Form.useWatch('color', form);
  const previewDescription = Form.useWatch('description', form);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subjects;
    return subjects.filter((s) => s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q));
  }, [subjects, search]);

  const openModal = (editing = null) => {
    setModal({ open: true, editing });
    form.setFieldsValue(editing
      ? { name: editing.name, description: editing.description || '', color: editing.color }
      : { name: '', description: '', color: nextSubjectColor(subjects) });
  };

  const closeModal = () => {
    setModal({ open: false, editing: null });
    form.resetFields();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        color: normalizeHex(values.color),
      };
      if (modal.editing) {
        await callCenterSubjectsApi.update(modal.editing.id, payload);
        message.success('موضوع ویرایش شد');
      } else {
        await callCenterSubjectsApi.create(payload);
        message.success('موضوع جدید اضافه شد');
      }
      await refreshCallSubjects();
      closeModal();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره موضوع');
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (subject) => {
    setDeletingId(subject.id);
    try {
      await callCenterSubjectsApi.delete(subject.id);
      message.success('موضوع حذف شد');
      await refreshCallSubjects();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف موضوع');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'موضوع',
      key: 'name',
      render: (_, record) => <CallSubjectChip subject={{ ...record, description: undefined }} />,
    },
    {
      title: 'توضیحات',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (text
        ? <span className="custom-font text-sm text-gray-600 dark:text-gray-300">{text}</span>
        : <span className="text-gray-300">—</span>),
    },
    {
      title: 'تعداد تماس',
      dataIndex: 'calls_count',
      key: 'calls_count',
      width: 130,
      render: (count) => (
        <Tag className="custom-font m-0" color={count ? 'blue' : 'default'}>
          {Number(count || 0).toLocaleString('fa-IR')} تماس
        </Tag>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <PermissionGate module="callcenter" permission="manage_callcenter_subjects">
          <Space size={2}>
            <Tooltip title="ویرایش">
              <Button type="text" icon={<EditOutlined />} onClick={() => openModal(record)} />
            </Tooltip>
            <Popconfirm
              title={<span className="custom-font">حذف موضوع «{record.name}»؟</span>}
              description={record.calls_count
                ? <span className="custom-font text-xs">{Number(record.calls_count).toLocaleString('fa-IR')} تماس بدون موضوع خواهند شد.</span>
                : undefined}
              onConfirm={() => remove(record)}
              okText="حذف"
              cancelText="انصراف"
              okButtonProps={{ danger: true }}
              placement="topRight"
            >
              <Tooltip title="حذف">
                <Button type="text" danger icon={<DeleteOutlined />} loading={deletingId === record.id} />
              </Tooltip>
            </Popconfirm>
          </Space>
        </PermissionGate>
      ),
    },
  ];

  return (
    <MainLayout
      title="موضوعات تماس"
      activeMenu="/call-center/subjects"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'موضوعات تماس' },
      ]}
    >
      <div className="w-full custom-font">
        <PermissionGate module="callcenter" permission="manage_callcenter_subjects" showWarning>
          <Card
            bordered={false}
            className="shadow-sm rounded-lg"
            styles={{ body: { padding: '1.5rem' } }}
            title={(
              <div className="flex items-center gap-2">
                <TagsOutlined className="text-gray-500" />
                <span className="font-semibold text-gray-800 dark:text-gray-100">موضوعات تماس</span>
                <span className="text-xs text-gray-400 font-normal hidden md:inline">
                  برچسب‌های رنگی برای دسته‌بندی و فیلتر تماس‌ها
                </span>
              </div>
            )}
            extra={(
              <Space wrap>
                <Input
                  allowClear
                  prefix={<SearchOutlined className="text-gray-400" />}
                  placeholder="جستجوی موضوع"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-48"
                />
                <Button icon={<ReloadOutlined />} onClick={reload}>به‌روزرسانی</Button>
                <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} icon={<PlusOutlined />} onClick={() => openModal()}>
                  افزودن موضوع
                </Button>
              </Space>
            )}
          >
            {loading && subjects.length === 0 ? (
              <TableSkeleton columns={4} rows={5} />
            ) : (
              <Table
                columns={columns}
                dataSource={filtered}
                rowKey="id"
                pagination={filtered.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                size="middle"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={(
                        <span className="custom-font text-gray-500">
                          {search ? 'موضوعی با این عبارت پیدا نشد.' : 'هنوز موضوعی تعریف نشده است.'}
                        </span>
                      )}
                    >
                      {!search && (
                        <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} icon={<PlusOutlined />} onClick={() => openModal()}>
                          ساخت اولین موضوع
                        </Button>
                      )}
                    </Empty>
                  ),
                }}
              />
            )}
          </Card>
        </PermissionGate>
      </div>

      <Modal
        title={<span className="custom-font">{modal.editing ? 'ویرایش موضوع تماس' : 'افزودن موضوع تماس'}</span>}
        open={modal.open}
        onCancel={closeModal}
        onOk={submit}
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" className="mt-4 custom-font" onFinish={submit}>
          <Form.Item
            name="name"
            label="نام موضوع"
            rules={[
              { required: true, whitespace: true, message: 'نام موضوع الزامی است' },
              { max: 100, message: 'حداکثر ۱۰۰ کاراکتر' },
            ]}
          >
            <Input placeholder="مثال: پیگیری فاکتور، شکایت، درخواست دمو" maxLength={100} autoFocus />
          </Form.Item>

          <Form.Item name="color" label="رنگ برچسب" rules={[{ required: true, message: 'رنگ را انتخاب کنید' }]}>
            <SubjectColorField />
          </Form.Item>

          <Form.Item name="description" label="توضیحات (اختیاری)" rules={[{ max: 500, message: 'حداکثر ۵۰۰ کاراکتر' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="چه تماس‌هایی در این موضوع قرار می‌گیرند؟" />
          </Form.Item>

          <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3">
            <span className="text-xs text-gray-500">پیش‌نمایش:</span>
            <CallSubjectChip
              subject={{
                name: previewName?.trim() || 'نام موضوع',
                color: normalizeHex(previewColor),
                description: previewDescription,
              }}
            />
          </div>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CallCenterSubjects;
